import { Suspense } from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import PlusIcon from '@/assets/icons/plus-sm.svg';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import {
  AddContractProvider,
  useAddContractContext,
} from '@/contexts/add-contract-context';
import { useSourceFreemium } from '@/hooks/use-source-freemium';
import { useSourceIndeedContract } from '@/hooks/use-source-indeed-contract';
import { useFreemiumBoardLock } from '@/hooks/use-freemium-board-lock';
import { PopularContractCards } from './popular-contract-cards';
import { IndeedPanelHost } from './indeed-panel';
import { IndeedConnectReturn } from './indeed-connect-return';
import { useIndeedConnectToasts } from './indeed-connect-toasts';

import { LockedOverlay } from '@/components/ui/locked-overlay';
import { ContractsList } from './contracts-list';
import { ContractForm } from './contract-form';
import { UpgradeCard } from './upgrade-card';
import { cn } from '@/utils/classnames';

function ContractsHeader({ disabled }: { disabled: boolean }) {
  const { setSheetOpen } = useAddContractContext();

  return (
    <div className="flex items-center justify-between gap-4">
      {/* Title and description */}
      <div className="flex flex-col gap-1">
        <Typography variant="h4" as="h1">
          <FormattedMessage
            defaultMessage="Contracts"
            description="Header for the contracts management section"
          />
        </Typography>
        <Typography variant="body1">
          <FormattedMessage
            defaultMessage="Manage your job board contracts. Active contracts appear as posting options when you post a job, so your team can use them without managing them separately."
            description="Explanation of how active job channel contracts work for the team"
          />
        </Typography>
      </div>

      {/* Button to open create contract modal */}
      <Button
        variant="secondary"
        type="button"
        onClick={() => setSheetOpen(true)}
        disabled={disabled}
        className="h-10"
      >
        <img src={PlusIcon} alt="" />
        <FormattedMessage
          defaultMessage="Add Contract"
          description="Button to add a new contract"
        />
      </Button>
    </div>
  );
}

function ContractsPage({
  disabled,
  children,
}: {
  disabled: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="flex h-full flex-col gap-6">
      <ContractsHeader disabled={disabled} />
      {children}
      {/* Grid lists out all vonq contracts */}
      <Suspense fallback={<ContractsList.Skeleton />}>
        <ContractsList disabled={disabled} />
      </Suspense>

      {/* Modal to create new contract */}
      <ContractForm />
    </div>
  );
}

export function Contracts() {
  const indeedContractEnabled = useSourceIndeedContract();
  if (indeedContractEnabled) return <IndeedContracts />;
  return <LegacyContracts />;
}

function IndeedContracts() {
  const isLocked = useSourceFreemium();
  const boardLock = useFreemiumBoardLock();
  const indeedConnectToasts = useIndeedConnectToasts();
  return (
    <div className={cn('flex flex-col gap-6', !isLocked && 'mt-4')}>
      {isLocked ? (
        boardLock ? (
          <AddContractProvider lock="boards">
            <FreemiumBoardBrowser />
          </AddContractProvider>
        ) : (
          <>
            <PopularContractCards locked />
            <UpgradeCard />
          </>
        )
      ) : (
        <AddContractProvider>
          <ContractsPage disabled={false}>
            <PopularContractCards />
          </ContractsPage>
        </AddContractProvider>
      )}
      <IndeedPanelHost />
      <IndeedConnectReturn onOutcome={indeedConnectToasts} />
    </div>
  );
}

function FreemiumBoardBrowser() {
  return (
    <>
      <ContractsHeader disabled={false} />
      <PopularContractCards locked />
      <UpgradeCard />
      <ContractForm />
    </>
  );
}

function LegacyContracts() {
  const intl = useIntl();
  const isLocked = useSourceFreemium();

  return (
    <div className={cn('flex flex-col gap-6', !isLocked && 'mt-4')}>
      {isLocked ? (
        <>
          <UpgradeCard />
          <LockedOverlay
            className="border-fx-neutral-200 h-[420px] w-full border p-4"
            message={intl.formatMessage({
              defaultMessage: 'Contracts are part of Source',
              description:
                'Overlay message on job board contracts that a free-tier account cannot manage; Source is the product name',
            })}
          >
            <AddContractProvider lock={isLocked ? 'all' : undefined}>
              <ContractsPage disabled={isLocked} />
            </AddContractProvider>
          </LockedOverlay>
        </>
      ) : (
        <AddContractProvider lock={isLocked ? 'all' : undefined}>
          <ContractsPage disabled={isLocked} />
        </AddContractProvider>
      )}
    </div>
  );
}
